import { useTranslationContext } from "context/translation";
import { useMemo } from "react";
import { boolean, date, object, string } from "yup";

export type RegisterMachineFormValues = {
  itemId: string;
  purchaseLocation: string;
  purchaseDate: Date | string | null;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  birthday: Date | null;
  emailOptIn: boolean;
  registrationEmailOptIn: boolean;
  terms: boolean;
};

const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

export const useMachineRegistrationForm = (currentUserId: string | null) => {
  const translations = useTranslationContext();

  const machineSchema = useMemo(() => {
    const startDate = new Date();
    startDate.setFullYear(new Date().getFullYear() - 2);
    const minAgeDate = new Date();
    minAgeDate.setFullYear(new Date().getFullYear() - 18);

    const machineInfoSchema = {
      itemId: string().required(translations?.machineIsRequired ?? ""),
      purchaseLocation: string().required(
        translations?.purchasePlaceIsRequired ?? "",
      ),
      purchaseDate: date()
        .transform((value) => {
          const result = new Date(value);
          return result;
        })
        .required(translations?.purchaseDateIsRequired ?? "")
        .min(startDate, translations?.purchaseDate2Years ?? "")
        .max(new Date(), translations?.purchaseDate2Years ?? ""),
    };

    const createUserSchema = {
      firstName: string().required(translations.firstNameIsRequired ?? ""),
      lastName: string().required(translations.lastNameIsRequired ?? ""),
      email: string()
        .required(translations.emailIsRequired ?? "")
        .email(translations.invalidEmail ?? ""),
      password: string()
        .required(translations.passwordIsRequired ?? "")
        .test(
          "password-strength",
          translations.invalidPassword ?? "",
          (value) => (value ? passwordRegex.test(value) : true),
        ),
      birthday: date()
        .transform((value) => {
          const result = new Date(value);
          return result;
        })
        .max(minAgeDate, translations.ageDisclaimerSubtext ?? ""),
    };

    const schema = object().shape({
      ...machineInfoSchema,
      ...(!currentUserId && createUserSchema),
      terms: boolean()
        .oneOf([true], translations.termsAreRequired ?? "")
        .required(translations.termsAreRequired ?? ""),
    });

    return schema;
  }, [translations, currentUserId]);

  return { machineSchema };
};
